// RFI measurements from GET /rfi/measurements turned into a sky scan for the map.
import { type SkyPoint } from './skyInterpolation'

export type RfiMeasurement = {
  id: number
  measuredAt: string
  azimuthDegrees: number
  elevationDegrees: number
  f10: number
  f30: number
  scanDirection: 1 | -1
}

export type Channel = 'f10' | 'f30'

export type DirectionFilter = 'both' | 'forward' | 'backward'

export type SkyScan = {
  step: number
  points: SkyPoint[]
}

// Scan azimuths are irregular (e.g. 30.58°, 32.6°), so the bilinear grid needs 1° cells.
const GRID_STEP = 1

const DIRECTION: Record<DirectionFilter, number | null> = { both: null, forward: 1, backward: -1 }

// f10/f30 are linear flux with a ~46 dB spread; interpolation and the colour scale work in dB.
const toDb = (power: number) => 10 * Math.log10(power)
const fromDb = (db: number) => 10 ** (db / 10)

// An unreadable time is left out rather than thrown, so one bad row does not take down the map.
function parseMeasuredAt(iso: string): Date | undefined {
  const time = new Date(iso)
  return Number.isNaN(time.getTime()) ? undefined : time
}

// Measurements are in SFU and only SFU is shown; dB stays internal to the colour scale.
// How a dB value from the scan is labelled: hover text, colour bar title and its ticks.
// eslint-disable-next-line no-unused-vars
type FormatValue = (db: number) => string
// eslint-disable-next-line no-unused-vars
type ColorbarTicks = (vmin: number, vmax: number) => { tickvals?: number[]; ticktext?: string[] }

export type ValueScale = {
  title: string
  format: FormatValue
  ticks: ColorbarTicks
}

const SFU_SIGNIFICANT_DIGITS = 3
const DB_PER_DECADE = 10

const formatSfu = (sfu: number) =>
  sfu >= 10 ** SFU_SIGNIFICANT_DIGITS
    ? Math.round(sfu).toLocaleString('en-US')
    : String(Number(sfu.toPrecision(SFU_SIGNIFICANT_DIGITS)))

// The colour scale stays logarithmic in SFU too: one tick per decade, labelled with the linear value.
function sfuDecadeTicks(vmin: number, vmax: number) {
  const tickvals: number[] = []
  for (let db = Math.ceil(vmin / DB_PER_DECADE) * DB_PER_DECADE; db <= vmax; db += DB_PER_DECADE) {
    tickvals.push(db)
  }
  return { tickvals, ticktext: tickvals.map((db) => formatSfu(fromDb(db))) }
}

export const SFU_SCALE: ValueScale = {
  title: 'SFU',
  format: (db) => `${formatSfu(fromDb(db))} SFU`,
  ticks: sfuDecadeTicks,
}

export function toSkyScan(measurements: RfiMeasurement[], channel: Channel, direction: DirectionFilter): SkyScan {
  const wanted = DIRECTION[direction]
  const points: SkyPoint[] = []

  for (const m of measurements) {
    if (wanted !== null && m.scanDirection !== wanted) continue
    const power = m[channel]
    if (!Number.isFinite(power) || power <= 0) continue // log undefined for NaN and non-positive values
    points.push({
      az: m.azimuthDegrees,
      el: m.elevationDegrees,
      value: toDb(power),
      time: parseMeasuredAt(m.measuredAt),
    })
  }

  return { step: GRID_STEP, points }
}

export function elevationRange(scan: SkyScan): [number, number] | null {
  if (!scan.points.length) return null
  let lo = Infinity
  let hi = -Infinity
  for (const p of scan.points) {
    lo = Math.min(lo, p.el)
    hi = Math.max(hi, p.el)
  }
  return [lo, hi]
}

// The scan is labelled in UTC like the rest of the telescope data.
// 'YYYY-MM-DD HH:MM:SS UTC', or without seconds for a period.
export function formatMeasuredAt(time: Date, withSeconds = true): string {
  const [date, clock] = time.toISOString().split('T')
  return `${date} ${clock.slice(0, withSeconds ? 8 : 5)} UTC`
}

// First and last measurement time of the scan, or null when no point has one.
export function measurementPeriod(scan: SkyScan): [Date, Date] | null {
  let first: Date | null = null
  let last: Date | null = null
  for (const p of scan.points) {
    if (!p.time) continue
    if (first === null || p.time.getTime() < first.getTime()) first = p.time
    if (last === null || p.time.getTime() > last.getTime()) last = p.time
  }
  if (first === null || last === null) return null
  return [first, last]
}

export function formatMeasurementPeriod([first, last]: [Date, Date]): string {
  const from = formatMeasuredAt(first, false).replace(' UTC', '')
  const to = formatMeasuredAt(last, false)
  return from === to.replace(' UTC', '') ? `Measured ${to}` : `Measured ${from} – ${to}`
}
